/**
 * PDF Save — one-click save of on-screen reports into the user's existing
 * OneDrive folder structure.
 *
 *   OneDrive > MICHEL - Personal > Documents > ACTS >
 *     1 - REPORTS MELVILLE MOTORS >
 *       2026 YEAR >
 *         WEEK 17 >
 *           STAFF 17 >
 *
 * We ask the user ONCE to pick the "1 - REPORTS MELVILLE MOTORS" parent
 * folder, then remember the handle in IndexedDB. Every subsequent save
 * re-uses that handle and auto-creates the nested "{year} YEAR" /
 * "WEEK {n}" / "STAFF {n}" subfolders as needed.
 *
 * PDF generation is delegated to the backend, which runs Puppeteer
 * (headless Chromium) against the HTML we send up. This is identical to
 * hitting Ctrl+P → Save as PDF in Chrome, so the output matches the
 * on-screen Print view exactly — no html2canvas quirks, no blank canvases.
 *
 * Browser support:
 *   - File System Access API: Chrome / Edge (desktop). Firefox / Safari
 *     fall back to a regular download via blob URL.
 */

// ── IndexedDB: persist the parent folder handle ──────────────────────────
const DB_NAME    = 'acts-detailing-pdf-save';
const DB_STORE   = 'handles';
const HANDLE_KEY = 'reports-parent-folder';

/**
 * Private work is a different business from the dealership's, and its
 * paperwork belongs in its own OneDrive folder — not buried inside
 * "1 - REPORTS MELVILLE MOTORS". The File System Access API can only ever
 * descend from a folder the user granted, never create a sibling of it, so
 * each vault is its own remembered handle and its own one-time pick.
 *
 * The dealership keeps the original key, so the folder already granted is
 * never asked for again.
 */
const vaultKey = (vault) =>
  (vault ? `${HANDLE_KEY}:${String(vault).toLowerCase()}` : HANDLE_KEY);

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(DB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// ── Folder handle management ─────────────────────────────────────────────
export function supportsFileSystemAccess() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

/** Check (and optionally request) read/write permission on a folder handle. */
async function ensurePermission(handle, { prompt = false } = {}) {
  if (!handle.queryPermission) return true; // older browser — assume OK
  const opts  = { mode: 'readwrite' };
  const state = await handle.queryPermission(opts);
  if (state === 'granted') return true;
  if (!prompt) return false;
  const req = await handle.requestPermission(opts);
  return req === 'granted';
}

/** Open the directory picker and remember the chosen handle. */
export async function pickAndRememberFolder(vault) {
  if (!supportsFileSystemAccess()) {
    throw new Error('Your browser does not support the File System Access API. Use Chrome or Edge.');
  }
  const handle = await window.showDirectoryPicker({
    // A separate picker id per vault so Chrome reopens each one where it was
    // last used, instead of sending you back to the dealership folder.
    id:      vault ? `acts-reports-${String(vault).toLowerCase()}` : 'acts-reports',
    mode:    'readwrite',
    startIn: 'documents',
  });
  await idbSet(vaultKey(vault), handle);
  return handle;
}

/** Retrieve remembered folder handle. Prompt if missing or permission lost.
 *
 * CRITICAL: this function calls `showDirectoryPicker()` and/or
 * `requestPermission()`, both of which consume the browser's transient user
 * activation. Always call it from the FIRST async step of a click handler —
 * do not interpose other awaits (fetches, IndexedDB writes, etc.) between
 * the click and this call.
 *
 * If permission is denied on a remembered handle we do NOT fall through to
 * the picker — that would call `showDirectoryPicker` after `requestPermission`
 * has already consumed the activation, producing the "Must be handling a
 * user gesture" error. Instead we throw so the caller can surface a clear
 * "click Change folder…" message.
 */
export async function getParentFolder({ forcePick = false, vault } = {}) {
  if (!supportsFileSystemAccess()) return null;
  if (forcePick) return pickAndRememberFolder(vault);

  let handle;
  try { handle = await idbGet(vaultKey(vault)); } catch { /* ignore */ }

  if (handle) {
    const ok = await ensurePermission(handle, { prompt: true });
    if (ok) return handle;
    // Permission request was denied/cancelled. Don't call showDirectoryPicker
    // now — the user gesture has been consumed. Forget the stale handle so
    // the next click starts from a clean slate.
    try { await idbDelete(vaultKey(vault)); } catch { /* ignore */ }
    const err = new Error(`Folder access was denied. Click "Change folder…" to pick the ${vault ? `${vault} ` : ''}reports folder again.`);
    err.name = 'NotAllowedError';
    throw err;
  }
  return pickAndRememberFolder(vault);
}

/** Forget the stored folder (admin control). */
export async function forgetParentFolder(vault) {
  try { await idbDelete(vaultKey(vault)); } catch { /* ignore */ }
}

/** Peek at the stored folder's name without prompting. Returns null if none. */
export async function getStoredFolderName(vault) {
  if (!supportsFileSystemAccess()) return null;
  let handle;
  try { handle = await idbGet(vaultKey(vault)); } catch { return null; }
  return (handle && handle.name) || null;
}

// ── Subfolder navigation ─────────────────────────────────────────────────
async function getOrCreateSubfolder(parent, name) {
  return parent.getDirectoryHandle(name, { create: true });
}

/** parent/{year} YEAR/WEEK {week} — returns the WEEK folder handle. */
export async function getWeekFolder(parent, year, week) {
  const yearFolder = await getOrCreateSubfolder(parent, `${year} YEAR`);
  const weekFolder = await getOrCreateSubfolder(yearFolder, `WEEK ${week}`);
  return weekFolder;
}

/** Inside the week folder, create STAFF {week}. */
export async function getStaffFolder(weekFolder, week) {
  return getOrCreateSubfolder(weekFolder, `STAFF ${week}`);
}

/** Buffing authorization folder.
 *  With a year + week it nests INSIDE the matching week folder —
 *    parent/{year} YEAR/WEEK {week}/BUFFING AUTORIZATION
 *  so each week's buffing PDFs sit alongside that week's paperwork
 *  instead of piling up in one giant flat folder at the parent root.
 *  Without year/week (e.g. an undated request) it falls back to the
 *  old flat parent/BUFFING AUTORIZATION location. */
export async function getBuffingFolder(parent, year, week) {
  if (year && week) {
    const weekFolder = await getWeekFolder(parent, year, week);
    return getOrCreateSubfolder(weekFolder, 'BUFFING AUTORIZATION');
  }
  return getOrCreateSubfolder(parent, 'BUFFING AUTORIZATION');
}

/** parent/RECEIPTS/<category> — returns the receipts subfolder for a category. */
export async function getReceiptsFolder(parent, category) {
  const root = await getOrCreateSubfolder(parent, 'RECEIPTS');
  // Sanitise category for filesystem safety. Keep spaces / & as-is — they're
  // legal in NTFS and the boss already uses spaces in his other folders.
  const safe = String(category || 'Other').replace(/[\\/:*?"<>|]/g, '_').trim();
  return getOrCreateSubfolder(root, safe);
}

/** parent/ASSETS/<kind> — receipts and photos for capital assets.
 *  `kind` is 'Receipts' or 'Photos' so they don't co-mingle.
 */
export async function getAssetsFolder(parent, kind = 'Receipts') {
  const root = await getOrCreateSubfolder(parent, 'ASSETS');
  const safe = String(kind || 'Receipts').replace(/[\\/:*?"<>|]/g, '_').trim() || 'Receipts';
  return getOrCreateSubfolder(root, safe);
}

// ── PDF generation (via backend Puppeteer) ───────────────────────────────

/**
 * Serialize an element into the HTML we send to the backend. Strips any
 * `.no-print` / `.reports-no-print` descendants first so they don't show
 * up in the PDF. Works on both live elements and detached clones.
 */
function serializeElement(element, stripSelectors) {
  // Clone so we can strip no-print items without touching the live DOM.
  const clone = element.cloneNode(true);
  stripSelectors.forEach(sel => {
    clone.querySelectorAll(sel).forEach(n => n.remove());
  });
  // The root of a single-section save (e.g. one employee's card) may carry
  // `reports-dept-section`, whose print rule is `page-break-before: always`.
  // On the very first element of a PDF that forces a blank leading page, so
  // drop it from the root. Inner sections (in a multi-section "Save All"
  // wrapper) keep theirs, preserving the page breaks BETWEEN them.
  if (clone.classList) clone.classList.remove('reports-dept-section');

  // If the live element has ancestors, re-wrap the clone in their class
  // chain so CSS selectors like `.reports-panel .dept-section` still match.
  if (element.isConnected) {
    let html = clone.outerHTML;
    let cur  = element.parentElement;
    while (cur && cur !== document.body && cur.tagName !== 'HTML') {
      const tag  = cur.tagName.toLowerCase();
      const cls  = cur.getAttribute('class');
      const id   = cur.id;
      const attrs = [
        id  ? `id="${escapeAttr(id)}"`   : '',
        cls ? `class="${escapeAttr(cls)}"` : '',
      ].filter(Boolean).join(' ');
      html = `<${tag}${attrs ? ' ' + attrs : ''}>${html}</${tag}>`;
      cur = cur.parentElement;
    }
    return html;
  }
  return clone.outerHTML;
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/**
 * Gather every stylesheet currently affecting the page, inline them into
 * one CSS string. Handles same-origin sheets (via cssRules) and cross-origin
 * ones (via fetch()) — the latter usually fails but is worth trying.
 */
async function gatherDocumentStyles() {
  const chunks = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules;
    try { rules = sheet.cssRules || sheet.rules; } catch { /* cross-origin */ }

    if (rules) {
      for (const rule of Array.from(rules)) chunks.push(rule.cssText);
      continue;
    }
    // Fallback: fetch the stylesheet text directly (works for same-origin
    // `<link rel=stylesheet>` that threw on cssRules access).
    if (sheet.href) {
      try {
        const res = await fetch(sheet.href, { credentials: 'same-origin' });
        if (res.ok) chunks.push(await res.text());
      } catch { /* give up */ }
    }
  }
  return chunks.join('\n');
}

/**
 * Render an element to a PDF Blob by POSTing its HTML + document CSS to
 * the backend's Puppeteer renderer.
 */
export async function elementToPdfBlob(element, {
  stripSelectors = ['.no-print', '.reports-no-print'],
  title          = 'Acts Detailing Report',
  format         = 'A4',
  orientation    = 'portrait',
  scale,
  margin,
} = {}) {
  if (!element) throw new Error('elementToPdfBlob: element is null');

  const [html, styles] = await Promise.all([
    Promise.resolve(serializeElement(element, stripSelectors)),
    gatherDocumentStyles(),
  ]);

  const token = localStorage.getItem('acts_token');
  // baseUrl lets Puppeteer resolve relative <img src="/acts-logo.png"> etc.
  // against the frontend origin — otherwise those images 404 inside the PDF.
  const baseUrl = window.location.origin + '/';
  const res = await fetch('/api/pdf/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ html, styles, title, format, orientation, baseUrl, scale, margin }),
  });

  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
    throw new Error(`PDF generation failed: ${msg}`);
  }

  const blob = await res.blob();
  if (blob.size < 1024) {
    console.warn('[pdfSave] Backend returned suspiciously small PDF:', blob.size, 'bytes');
  }
  return blob;
}

/** Write a blob into a folder handle. Overwrites if a file of the same name exists. */
export async function writeBlobToFolder(folder, filename, blob) {
  const fileHandle = await folder.getFileHandle(filename, { create: true });
  const writable   = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/** Fallback download when File System Access isn't available. */
function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href    = url;
  a.download= filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * High-level helper:
 *   savePdfSmart(element, filename, { year, week, staff, parent })
 *
 *   - With File System Access: saves to parent/{year} YEAR/WEEK {week}/[STAFF {week}/]filename
 *   - Without: triggers a regular download named `filename`
 *
 * `parent` — OPTIONAL pre-resolved FileSystemDirectoryHandle. When the caller
 * is about to save multiple PDFs in a loop (e.g. "Save All"), it MUST resolve
 * the parent once up-front (via `getParentFolder`) inside its click handler
 * and pass it in here. Otherwise every save tries to re-check permissions,
 * and after the first one consumes the user gesture, the rest fail with:
 *   "Must be handling a user gesture to show a file picker"
 *
 * Pass `parent: null` explicitly to force the plain-download fallback.
 *
 * Returns { ok, path, fallback }.
 */
export async function savePdfSmart(element, filename, {
  year, week, staff = false, parent: parentOverride, pdf: pdfOpts, vault,
} = {}) {
  // IMPORTANT: resolve the folder handle BEFORE generating the PDF.
  //
  // showDirectoryPicker() / requestPermission() both require a "live" user
  // gesture. If we await a long-running network call (like the PDF fetch)
  // first, the browser marks the gesture as consumed and blocks the picker.
  //
  // If the caller supplied `parent` we skip resolution entirely — that's the
  // recommended path for loops like "Save All".
  let parent;
  if (parentOverride !== undefined) {
    parent = parentOverride;
  } else if (supportsFileSystemAccess()) {
    try { parent = await getParentFolder({ vault }); } catch { parent = null; }
  } else {
    parent = null;
  }

  const blob = await elementToPdfBlob(element, pdfOpts);

  if (!parent) {
    downloadBlob(filename, blob);
    return { ok: true, path: filename, fallback: true };
  }

  let folder;
  const pathParts = [parent.name];
  if (year && week) {
    folder = await getWeekFolder(parent, year, week);
    pathParts.push(`${year} YEAR`, `WEEK ${week}`);
    if (staff) {
      folder = await getStaffFolder(folder, week);
      pathParts.push(`STAFF ${week}`);
    }
  } else {
    folder = parent;
  }

  await writeBlobToFolder(folder, filename, blob);
  pathParts.push(filename);
  return { ok: true, path: pathParts.join(' / '), fallback: false };
}

// ── Filename builders ────────────────────────────────────────────────────
/** DD-MM-YYYY from an ISO date string (YYYY-MM-DD). */
export function formatDateDMY(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

export function paperworkFilename(scope, isoDate) {
  // scope: 'ALL' | 'USED' | 'KIA' | 'SUZUKI' | 'GWM' | 'SERVICE' | etc.
  return `${scope}_Paperwork_${formatDateDMY(isoDate)}.pdf`;
}

export function weeklySummaryFilename(scope, week, year) {
  const w = String(week).padStart(2, '0');
  return scope === 'Weekly'
    ? `Weekly_Summary_W${w}-${year}.pdf`
    : `${scope}_Weekly_Summary_W${w}-${year}.pdf`;
}

export function staffWeeklyFilename(employeeName, week, year) {
  const name = (employeeName || 'STAFF').toUpperCase().replace(/\s+/g, '_');
  const w = String(week).padStart(2, '0');
  return `${name}_Weekly_Summary_W${w}-${year}.pdf`;
}

export function staffInvoiceFilename(employeeName, week, year) {
  const name = (employeeName || 'STAFF').toUpperCase().replace(/\s+/g, '_');
  const w = String(week).padStart(2, '0');
  return `${name}_Invoice_W${w}-${year}.pdf`;
}

/** DATE_MODEL_STOCK format for buffing authorizations.
 *  e.g. 30-04-2026_TASMAN_K007272.pdf
 */
export function buffingAuthFilename(date, model, stockNumber) {
  const d     = formatDateDMY(date) || 'NODATE';
  const sani  = s => String(s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const mdl   = sani(model)        || 'UNKNOWN';
  const stk   = sani(stockNumber)  || 'NOSTOCK';
  return `${d}_${mdl}_${stk}.pdf`;
}
